/**
 * @file FilterBar component - Rich filter builder with nested queries
 * @version 1.0.0
 */

import Filter from './Filter.js';

const { ref, watch, computed } = Vue;

// Generate unique IDs for filters
let filterId = 0;
function generateFilterId() {
  return `filter_${filterId++}`;
}

// Convert Mango selector to internal format
function mangoToInternal(selector) {
  if (!selector || Object.keys(selector).length === 0) {
    return { type: 'logical', operator: '$and', value: [], root: true };
  }

  let result;
  if (selector.$and) {
    result = {
      type: 'logical',
      operator: '$and',
      value: selector.$and.map(convertCondition),
    };
  } else if (selector.$or) {
    result = {
      type: 'logical',
      operator: '$or',
      value: selector.$or.map(convertCondition),
    };
  } else if (selector.$not) {
    result = {
      type: 'logical',
      operator: '$not',
      value: [convertCondition(selector.$not)],
    };
  } else {
    const conditions = Object.entries(selector).map(([field, fieldValue]) =>
      convertFieldCondition(field, fieldValue)
    );
    result = { type: 'logical', operator: '$and', value: conditions };
  }

  // Mark root with root: true if it's $and, otherwise wrap in $and
  if (result.operator === '$and') {
    result.root = true;
    return result;
  } else {
    return { type: 'logical', operator: '$and', value: [result], root: true };
  }
}

function convertCondition(condition) {
  if (condition.$and) {
    return {
      _id: generateFilterId(),
      type: 'logical',
      operator: '$and',
      value: condition.$and.map(convertCondition),
    };
  }
  if (condition.$or) {
    return {
      _id: generateFilterId(),
      type: 'logical',
      operator: '$or',
      value: condition.$or.map(convertCondition),
    };
  }
  if (condition.$not) {
    return {
      _id: generateFilterId(),
      type: 'logical',
      operator: '$not',
      value: [convertCondition(condition.$not)],
    };
  }

  const field = Object.keys(condition)[0];
  return convertFieldCondition(field, condition[field]);
}

function convertFieldCondition(field, value) {
  if (typeof value === 'object' && !Array.isArray(value)) {
    const op = Object.keys(value)[0];
    const opValue = value[op];
    return {
      _id: generateFilterId(),
      type: 'field',
      operator: op,
      parameter: field,
      value: opValue,
    };
  }
  return {
    _id: generateFilterId(),
    type: 'field',
    operator: '$eq',
    parameter: field,
    value,
  };
}

// Convert internal format to Mango selector
function internalToMango(internal) {
  if (!internal || !internal.operator) {
    return {};
  }

  const { operator, value, parameter, root } = internal;

  // If root $and with no children, return empty selector
  if (root && operator === '$and' && (!value || value.length === 0)) {
    return {};
  }

  // If root $and with only one child, unwrap it
  if (root && operator === '$and' && value && value.length === 1) {
    return internalToMango(value[0]);
  }

  if (operator === '$and' || operator === '$or') {
    if (!value || value.length === 0) {
      return {};
    }
    const conditions = value
      .map(internalToMango)
      .filter(c => Object.keys(c).length > 0);
    if (conditions.length === 0) {
      return {};
    }
    return { [operator]: conditions };
  }

  if (operator === '$not') {
    if (!value || value.length === 0) {
      return {};
    }
    return { $not: internalToMango(value[0]) };
  }

  // Field filter - check if complete
  if (parameter === null || value === null) {
    return {};
  }

  if (operator === '$eq') {
    return { [parameter]: value };
  }

  return { [parameter]: { [operator]: value } };
}

export default {
  name: 'FilterBar',

  components: {
    Filter,
  },

  props: {
    schema: {
      type: Object,
      required: true,
    },
    query: {
      type: Object,
      default: () => ({ selector: {} }),
    },
  },

  emits: ['update:query'],

  setup(props, { emit }) {
    const internal = ref(mangoToInternal(props.query.selector));

    // Track if we're emitting to avoid feedback loops
    let isEmitting = false;

    // Watch internal changes and emit
    watch(
      internal,
      newVal => {
        isEmitting = true;
        emit('update:query', { selector: internalToMango(newVal) });
        // Reset flag after Vue processes the update
        Vue.nextTick(() => {
          isEmitting = false;
        });
      },
      { deep: true }
    );

    // Watch external query changes (e.g., from FilterHeader)
    watch(
      () => props.query?.selector,
      newSelector => {
        if (isEmitting) return; // Skip if this is our own emit
        internal.value = mangoToInternal(newSelector);
      },
      { deep: true }
    );

    // Reset filters when schema changes
    watch(
      () => props.schema,
      () => {
        internal.value = {
          type: 'logical',
          operator: '$and',
          value: [],
          root: true,
        };
        emit('update:query', { selector: {} });
      }
    );

    const clearAll = () => {
      internal.value = {
        type: 'logical',
        operator: '$and',
        value: [],
        root: true,
      };
    };

    const hasFilters = computed(() => {
      return internal.value.value && internal.value.value.length > 0;
    });

    return {
      internal,
      clearAll,
      hasFilters,
    };
  },

  template: `
    <div class="d-flex align-items-center gap-2 flex-wrap">
      <Filter :filter="internal" :schema="schema" />
      <button
        v-if="hasFilters"
        @click="clearAll"
        class="btn btn-sm btn-outline-danger"
        type="button"
      >
        <i class="bi-x"></i> Clear All
      </button>
    </div>
  `,
};
